import { useFormik } from 'formik';
import React, { useState } from 'react';
import { useNavigate } from 'react-router';
import * as yup from 'yup';

import { AuthShell } from '@/components/auth/AuthShell';
import { Button, Field, TextInput } from '@/components/ds';
import { useAuthContext } from '@/context/AuthProvider';
import { useTranslationPrefix } from '@/translations/i18n';

const RequestPasswordReset: React.FC = () => {
  const t = useTranslationPrefix('reset_password.request');
  const validationT = useTranslationPrefix('validation');
  const navigate = useNavigate();
  const { resetPassword, isResettingPassword } = useAuthContext();

  const [error, setError] = useState<string>();

  const handleRequestPasswordReset = async (values: { email: string }) => {
    try {
      setError(undefined);
      await resetPassword(values.email);
      navigate('/password-reset/request-sent', {
        state: { email: values.email },
      });
    } catch (error: any) {
      setError(error.message);
    }
  };

  const handleBackToLogIn = () => {
    navigate('/login');
  };

  const validationSchema = yup.object({
    email: yup
      .string()
      .email(validationT('valid', { name: t('email') }))
      .required(validationT('required', { name: t('email') })),
  });

  const formik = useFormik({
    initialValues: {
      email: '',
    },
    enableReinitialize: true,
    validationSchema,
    validateOnBlur: false,
    onSubmit: handleRequestPasswordReset,
  });

  const emailError = formik.touched.email && formik.errors.email;

  return (
    <AuthShell
      title="Reset password"
      footer={
        // a button, not a link, so it can stand down while the request is in flight
        <button
          type="button"
          className="hx-auth-link"
          disabled={isResettingPassword}
          onClick={handleBackToLogIn}
          data-testid="password-reset-back-to-login-btn">
          {t('back_to_login')}
        </button>
      }>
      <style>{`.hx-auth-field-error { font-size: 11.5px; font-weight: 500; color: #DB5282; }`}</style>
      <form onSubmit={formik.handleSubmit} data-testid="request-password-reset-form">
        <h1 className="hx-auth-title">{t('title')}</h1>
        <p className="hx-auth-sub">
          {t('intro.0')} {t('intro.1')}
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <Field label={t('email')} htmlFor="password-reset-email">
            <TextInput
              id="password-reset-email"
              name="email"
              // no type="email": native validation would pre-empt formik's messages
              inputMode="email"
              autoComplete="email"
              autoCapitalize="off"
              autoFocus
              placeholder="you@example.com"
              value={formik.values.email}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={Boolean(emailError)}
              inputProps={{ 'aria-label': t('email'), 'data-testid': 'password-reset-email-input' }}
            />
            {emailError ? <span className="hx-auth-field-error">{emailError}</span> : null}
          </Field>
          {error && <p className="hx-auth-error">{error}</p>}
          <Button
            type="submit"
            variant="primary"
            block
            disabled={isResettingPassword}
            data-testid="password-reset-submit-btn">
            {t('submit')}
          </Button>
        </div>
      </form>
    </AuthShell>
  );
};

export default RequestPasswordReset;
